/**
 * Sophia chat API — SSE stream + blocking fallback.
 *
 * Ported from apps/dashboard-multitenant/frontend/src/api.ts to reuse the
 * exact same protocol (start/progress/done/error frames) served by
 * /api/sophia/stream on the platform-v2 backend.
 *
 * Auth model: unlike the dashboard-multitenant which uses same-origin
 * cookies, platform-v2 uses Bearer JWT + X-Tenant header, so we accept
 * a `getAuthHeaders()` callback the caller wires from useApi.
 */

const FRIENDLY_NETWORK_MESSAGE =
  "Não consegui falar com a Sophia agora. Tente novamente.";

export interface AssistantResponse {
  ok?: boolean;
  message?: string;
  next_state?: Record<string, any>;
  state_patch?: Record<string, any>;
  chart_actions?: Array<{
    chart_id?: string;
    action?: string;
    range?: any;
    [k: string]: any;
  }>;
  metadata?: Record<string, any>;
}

export interface StreamHandlers {
  signal?: AbortSignal;
  onStart?: (payload: { request_id?: string } & Record<string, unknown>) => void;
  onProgress?: (phase: string) => void;
  /** Texto parcial da resposta, conforme o modelo escreve. */
  onDelta?: (text: string) => void;
  onDone: (result: AssistantResponse) => void | Promise<void>;
  onError: (message: string) => void;
}

export interface AuthHeadersProvider {
  (): Record<string, string>;
}

type SseFrame = { event: string; data: string };

function parseSseFrame(rawFrame: string): SseFrame | null {
  const lines = rawFrame.split("\n");
  let event = "";
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice("event:".length).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice("data:".length).trim());
  }
  if (!event && !dataLines.length) return null;
  return { event, data: dataLines.join("\n") };
}

function parseJsonObject(data: string): Record<string, unknown> {
  if (!data) return {};
  try {
    const parsed = JSON.parse(data) as unknown;
    if (parsed && typeof parsed === "object") return parsed as Record<string, unknown>;
  } catch { /* ignore */ }
  return {};
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

async function extractErrorMessage(response: Response): Promise<string> {
  const text = await response.text();
  if (text) {
    try {
      const parsed = JSON.parse(text) as { message?: unknown; error?: unknown };
      if (parsed && typeof parsed.message === "string" && parsed.message.trim()) return parsed.message;
      if (parsed && typeof parsed.error === "string" && parsed.error.trim()) return parsed.error;
    } catch {/* ignore */}
    return text;
  }
  return `HTTP ${response.status}`;
}

export async function sendAssistantMessage(
  message: string,
  opts: {
    dashboardId?: string;
    state?: Record<string, any>;
    getAuthHeaders: AuthHeadersProvider;
    signal?: AbortSignal;
  },
): Promise<AssistantResponse> {
  const body: Record<string, any> = { message };
  if (opts.dashboardId) body.dashboard_id = opts.dashboardId;
  if (opts.state) body.state = opts.state;

  const res = await fetch("/api/sophia/message", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...opts.getAuthHeaders(),
    },
    body: JSON.stringify(body),
    signal: opts.signal,
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return (await res.json()) as AssistantResponse;
}

export async function streamAssistantMessage(
  message: string,
  opts: {
    dashboardId?: string;
    state?: Record<string, any>;
    getAuthHeaders: AuthHeadersProvider;
  },
  handlers: StreamHandlers,
): Promise<void> {
  const body: Record<string, any> = { message };
  if (opts.dashboardId) body.dashboard_id = opts.dashboardId;
  if (opts.state) body.state = opts.state;

  let response: Response;
  try {
    response = await fetch("/api/sophia/stream", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...opts.getAuthHeaders(),
      },
      body: JSON.stringify(body),
      signal: handlers.signal,
    });
  } catch (error) {
    if (isAbortError(error)) return;
    handlers.onError(FRIENDLY_NETWORK_MESSAGE);
    return;
  }

  if (!response.ok) {
    handlers.onError(await extractErrorMessage(response));
    return;
  }
  if (!response.body) throw new Error("ReadableStream indisponível");

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finished = false;

  const dispatchFrame = (frame: SseFrame) => {
    if (frame.event === "start") {
      handlers.onStart?.(parseJsonObject(frame.data) as { request_id?: string } & Record<string, unknown>);
      return;
    }
    if (frame.event === "delta") {
      const payload = parseJsonObject(frame.data) as { text?: unknown };
      if (typeof payload.text === "string" && payload.text) {
        handlers.onDelta?.(payload.text);
      }
      return;
    }
    if (frame.event === "progress") {
      const payload = parseJsonObject(frame.data) as { phase?: unknown };
      if (typeof payload.phase === "string" && payload.phase.trim()) {
        handlers.onProgress?.(payload.phase);
      }
      return;
    }
    if (frame.event === "done") {
      finished = true;
      handlers.onDone(parseJsonObject(frame.data) as AssistantResponse);
      return;
    }
    if (frame.event === "error") {
      finished = true;
      const payload = parseJsonObject(frame.data) as { message?: unknown };
      const friendly =
        typeof payload.message === "string" && payload.message.trim()
          ? payload.message
          : FRIENDLY_NETWORK_MESSAGE;
      handlers.onError(friendly);
    }
  };

  const drainBuffer = () => {
    let sepIndex = buffer.indexOf("\n\n");
    while (sepIndex !== -1) {
      const rawFrame = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);
      const frame = parseSseFrame(rawFrame);
      if (frame) dispatchFrame(frame);
      sepIndex = buffer.indexOf("\n\n");
    }
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      drainBuffer();
      if (finished) break;
    }
    if (!finished) {
      buffer += decoder.decode();
      drainBuffer();
    }
  } catch (error) {
    if (isAbortError(error)) return;
    if (!finished) handlers.onError(FRIENDLY_NETWORK_MESSAGE);
    return;
  } finally {
    reader.cancel().catch(() => {/* already closed */});
  }

  if (!finished) handlers.onError(FRIENDLY_NETWORK_MESSAGE);
}
